import { useMemo, useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { eur } from "@/lib/api";
import { agentColorHex } from "@/lib/agentColors";
import { TrendingUp, Check } from "lucide-react";

const AGENCY_COLORS = ["#0284C7", "#38BDF8", "#0369A1", "#22D3EE", "#60A5FA", "#2563EB", "#7DD3FC", "#155E75"];
const YEAR_COLORS = ["#94A3B8", "#0EA5E9", "#F59E0B", "#10B981", "#8B5CF6"];
const MONTHS_IT = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

function YoyTooltip({ active, payload, label, years }) {
  if (!active || !payload?.length) return null;
  const byYear = {};
  payload.forEach((p) => { byYear[p.dataKey] = p.value; });
  const latest = years[years.length - 1], prev = years[years.length - 2];
  const lv = byYear[latest] ?? 0, pv = byYear[prev] ?? 0;
  const delta = pv > 0 ? Math.round(((lv - pv) / pv) * 100) : null;
  return (
    <div style={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12, padding: "8px 10px" }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ color: p.color }}>{p.dataKey}: {eur(p.value)}</div>
      ))}
      {delta != null && (
        <div style={{ marginTop: 4, fontWeight: 700, color: delta >= 0 ? "#059669" : "#DC2626" }}>
          {delta >= 0 ? "+" : ""}{delta}% vs {prev}
        </div>
      )}
    </div>
  );
}

export default function AgencyTrendChart({ trend = [], agencies = [], yoy = [], yoyYears = [], yoyByAgency = {}, view: viewProp, onViewChange }) {
  const [month, setMonth] = useState("all");
  const [selected, setSelected] = useState([]);
  const [viewState, setViewState] = useState("periodo"); // periodo | yoy
  const view = viewProp ?? viewState;
  const setView = onViewChange || setViewState;
  const [yoyAgency, setYoyAgency] = useState("all");

  const rows = useMemo(() => trend
    .filter((r) => month === "all" || r.month.slice(5, 7) === month)
    .map((r) => ({ ...r, label: `${MONTHS_IT[parseInt(r.month.slice(5, 7), 10) - 1]} '${r.month.slice(2, 4)}` })), [trend, month]);

  const yoyData = useMemo(() => (yoyAgency === "all" ? yoy : (yoyByAgency[yoyAgency] || [])), [yoy, yoyByAgency, yoyAgency]);
  const yoyRows = useMemo(() => yoyData.map((r) => ({ ...r, label: MONTHS_IT[parseInt(r.m, 10) - 1] })), [yoyData]);
  const yoyAgencies = useMemo(() => Object.keys(yoyByAgency).sort(), [yoyByAgency]);

  const yoySummary = useMemo(() => {
    if (yoyYears.length < 2) return null;
    const latest = yoyYears[yoyYears.length - 1], prev = yoyYears[yoyYears.length - 2];
    let lv = 0, pv = 0;
    yoyData.forEach((r) => { if ((r[latest] || 0) > 0) { lv += r[latest] || 0; pv += r[prev] || 0; } });
    const delta = pv > 0 ? Math.round(((lv - pv) / pv) * 100) : null;
    return { latest, prev, delta };
  }, [yoyData, yoyYears]);

  const toggle = (a) => setSelected((s) => (s.includes(a) ? s.filter((x) => x !== a) : [...s, a]));
  const colorFor = (a) => agentColorHex(a);
  const isYoY = view === "yoy" && yoyYears.length > 0;

  return (
    <div data-testid="agency-trend-chart">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-primary" />
          <h2 className="font-heading text-lg font-bold tracking-tight">
            {isYoY ? `Confronto vendite anno su anno${yoyAgency !== "all" ? ` · ${yoyAgency}` : ""}` : "Andamento vendite: totale mensile, cumulato e per agenzia"}
          </h2>
          {isYoY && yoySummary?.delta != null && (
            <span data-testid="yoy-delta-badge" className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${yoySummary.delta >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
              {yoySummary.delta >= 0 ? "+" : ""}{yoySummary.delta}% vs {yoySummary.prev}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Select value={view} onValueChange={setView}>
            <SelectTrigger className="h-9 w-[190px]" data-testid="trend-view-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="periodo">Andamento del periodo</SelectItem>
              <SelectItem value="yoy" disabled={yoyYears.length < 2}>Confronto anno precedente</SelectItem>
            </SelectContent>
          </Select>
          {!isYoY && (
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="h-9 w-[130px]" data-testid="trend-month-select"><SelectValue placeholder="Mese" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i mesi</SelectItem>
                {MONTHS_IT.map((m, i) => <SelectItem key={m} value={String(i + 1).padStart(2, "0")}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {isYoY && (
            <Select value={yoyAgency} onValueChange={setYoyAgency}>
              <SelectTrigger className="h-9 w-[190px]" data-testid="yoy-agency-select"><SelectValue placeholder="Agenzia" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le agenzie</SelectItem>
                {yoyAgencies.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={300}>
        {isYoY ? (
          <LineChart data={yoyRows} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
            <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
            <Tooltip content={<YoyTooltip years={yoyYears} />} />
            <Legend wrapperStyle={{ fontSize: 11 }} iconType="plainline" />
            {yoyYears.map((y, i) => (
              <Line key={y} type="monotone" dataKey={y} name={y}
                stroke={YEAR_COLORS[i % YEAR_COLORS.length]} strokeWidth={i === yoyYears.length - 1 ? 3 : 2}
                strokeDasharray={i === yoyYears.length - 1 ? undefined : "5 4"} dot={false} />
            ))}
          </LineChart>
        ) : (
          <LineChart data={rows} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
            <YAxis yAxisId="left" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
            <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} formatter={(v, name) => [eur(v), name === "cumulato" ? "Cumulato" : name === "totale" ? "Totale mensile" : name]} />
            <Legend wrapperStyle={{ fontSize: 11 }} iconType="plainline" />
            {selected.map((a) => (
              <Line key={a} yAxisId="left" type="monotone" dataKey={a} name={a} stroke={colorFor(a)} strokeWidth={2} dot={false} />
            ))}
            <Line yAxisId="left" type="monotone" dataKey="totale" name="Totale mensile" stroke="#0EA5E9" strokeWidth={3} dot={false} />
            <Line yAxisId="right" type="monotone" dataKey="cumulato" name="Cumulato" stroke="hsl(var(--foreground))" strokeWidth={2.5} strokeDasharray="5 4" dot={false} />
          </LineChart>
        )}
      </ResponsiveContainer>

      {isYoY && (
        <p className="mt-3 text-[11px] leading-snug text-muted-foreground" data-testid="yoy-note">
          <span className="font-semibold text-foreground">Nota:</span> il grafico include l'intero storico d'acquisto dei clienti oggi in portafoglio, compresi gli anni precedenti all'attivazione dell'agenzia. I valori di quegli anni si riferiscono quindi ad acquisti fatti dai clienti prima che entrassero nel portafoglio attuale.
        </p>
      )}

      {!isYoY && (
        <div className="mt-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Confronta agenzie</span>
            <div className="flex gap-2 text-xs">
              <button className="text-primary hover:underline" onClick={() => setSelected(agencies)} data-testid="trend-select-all">Tutte</button>
              <button className="text-muted-foreground hover:underline" onClick={() => setSelected([])} data-testid="trend-select-none">Nessuna</button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {agencies.map((a) => {
              const active = selected.includes(a);
              return (
                <button key={a} onClick={() => toggle(a)} data-testid={`trend-agency-${a}`}
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${active ? "border-transparent text-white" : "border-border bg-transparent text-muted-foreground hover:bg-accent"}`}
                  style={active ? { background: colorFor(a) } : undefined}>
                  {active && <Check className="h-3 w-3" />}
                  <span className={active ? "" : "inline-flex items-center gap-1.5"}>
                    {!active && <span className="inline-block h-2 w-2 rounded-full" style={{ background: colorFor(a) }} />}
                    {a}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
